import { FACTORY_ADDRESS } from '@pancakeswap/sdk'
import { getUnixTime, sub } from 'date-fns'
import Link from 'next/link'
import { gql } from 'graphql-request'
import { GetStaticProps } from 'next'
import { SWRConfig } from 'swr'
import { bitQueryServerClient, infoServerClient } from 'utils/graphql'
import { getBlocksFromTimestamps } from 'views/Info/hooks/useBlocksFromTimestamps'
import { getCakeVaultAddress } from 'utils/addressHelpers'
import { getCakeContract } from 'utils/contractHelpers'
import { formatEther } from '@ethersproject/units'

const IndexPage = ({ totalTx30Days, addressCount30Days, tvl }) => {
  return (
    <SWRConfig
      value={{
        fallback: {
          totalTx30Days,
          addressCount30Days,
          tvl,
        },
      }}
    >
      <main className="mx-auto w-full max-w-4xl px-4 py-6 sm:px-6 sm:py-10">
        <section className="rounded-[28px] border border-white/10 bg-zinc-950 p-5 sm:p-8">
          <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-widest text-orange-400">CRP Exchange</p>
              <h1 className="text-3xl font-medium tracking-tight text-white sm:text-4xl">Choose your swap</h1>
              <p className="mt-3 text-sm text-zinc-400">Select a pair to start trading.</p>
            </div>
            <span className="rounded-full border border-orange-500/20 bg-orange-500/10 px-4 py-2 text-xs text-orange-400">
              BNB Smart Chain
            </span>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {[
              {
                input: 'BNB',
                output: '0x86333e34c8804532Ae34207c46e741cBc3Ff3FE3',
                asset: '/bnb-crp.png',
                label: 'BNB to CRP',
              },
              {
                input: '0x86333e34c8804532Ae34207c46e741cBc3Ff3FE3',
                output: 'BNB',
                asset: '/crp-bnb.png',
                label: 'CRP to BNB',
              },
              {
                input: '0x55d398326f99059fF775485246999027B3197955',
                output: '0x86333e34c8804532Ae34207c46e741cBc3Ff3FE3',
                asset: '/usdt-crp.png',
                label: 'USDT to CRP',
              },
              {
                input: '0x86333e34c8804532Ae34207c46e741cBc3Ff3FE3',
                output: '0x55d398326f99059fF775485246999027B3197955',
                asset: '/crp-usdt.png',
                label: 'CRP to USDT',
              },
            ].map(({ input, output, asset, label }) => (
              <Link key={label} href={`/swap?inputCurrency=${input}&outputCurrency=${output}`}>
                <a
                  href={`/swap?inputCurrency=${input}&outputCurrency=${output}`}
                  aria-label={label}
                  className="group overflow-hidden rounded-2xl border border-white/10 bg-zinc-900 p-3 transition-colors hover:border-orange-500/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-500 sm:p-4"
                >
                  <img src={asset} alt={label} className="h-auto w-full" />
                  <div className="mt-3 flex items-center justify-between border-t border-white/10 pt-3 text-sm text-zinc-300">
                    <span>{label}</span>
                    <span aria-hidden="true" className="text-orange-400">
                      ↗
                    </span>
                  </div>
                </a>
              </Link>
            ))}
          </div>
        </section>
      </main>
    </SWRConfig>
  )
}

// Values fetched from TheGraph and BitQuery jan 24, 2022
const txCount = 54780336
const addressCount = 4425459

const tvl = 6082955532.115718

export const getStaticProps: GetStaticProps = async () => {
  const totalTxQuery = gql`
    query TotalTransactions($id: ID!, $block: Block_height) {
      pancakeFactory(id: $id, block: $block) {
        totalTransactions
      }
    }
  `

  const days30Ago = sub(new Date(), { days: 30 })

  const results = {
    totalTx30Days: txCount,
    addressCount30Days: addressCount,
    tvl,
  }

  if (process.env.SF_HEADER) {
    try {
      const [days30AgoBlock] = await getBlocksFromTimestamps([getUnixTime(days30Ago)])

      if (!days30AgoBlock) {
        throw new Error('No block found for 30 days ago')
      }

      const totalTx = await infoServerClient.request(totalTxQuery, {
        id: FACTORY_ADDRESS,
      })
      const totalTx30DaysAgo = await infoServerClient.request(totalTxQuery, {
        block: {
          number: days30AgoBlock.number,
        },
        id: FACTORY_ADDRESS,
      })

      if (
        totalTx?.pancakeFactory?.totalTransactions &&
        totalTx30DaysAgo?.pancakeFactory?.totalTransactions &&
        parseInt(totalTx.pancakeFactory.totalTransactions) > parseInt(totalTx30DaysAgo.pancakeFactory.totalTransactions)
      ) {
        results.totalTx30Days =
          parseInt(totalTx.pancakeFactory.totalTransactions) -
          parseInt(totalTx30DaysAgo.pancakeFactory.totalTransactions)
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'production') {
        console.error('Error when fetching total tx count', error)
      }
    }
  }

  const usersQuery = gql`
    query userCount($since: ISO8601DateTime, $till: ISO8601DateTime) {
      ethereum(network: bsc) {
        dexTrades(exchangeName: { in: ["Pancake", "Pancake v2"] }, date: { since: $since, till: $till }) {
          count(uniq: senders)
        }
      }
    }
  `

  if (process.env.BIT_QUERY_HEADER) {
    try {
      const result = await bitQueryServerClient.request(usersQuery, {
        since: days30Ago.toISOString(),
        till: new Date().toISOString(),
      })
      if (result?.ethereum?.dexTrades?.[0]?.count) {
        results.addressCount30Days = result.ethereum.dexTrades[0].count
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'production') {
        console.error('Error when fetching address count', error)
      }
    }
  }

  try {
    const result = await infoServerClient.request(gql`
      query tvl {
        pancakeFactories(first: 1) {
          totalLiquidityUSD
        }
        token(id: "0x0e09fabb73bd3ade0a17ecc321fd13a19e81ce82") {
          derivedUSD
        }
      }
    `)
    const { totalLiquidityUSD } = result.pancakeFactories[0]
    const cakeVaultV2 = getCakeVaultAddress()
    const cakeContract = getCakeContract()
    const totalCakeInVault = await cakeContract.balanceOf(cakeVaultV2)
    results.tvl = parseFloat(formatEther(totalCakeInVault)) * result.token.derivedUSD + parseFloat(totalLiquidityUSD)
  } catch (error) {
    if (process.env.NODE_ENV === 'production') {
      console.error('Error when fetching tvl stats', error)
    }
  }

  return {
    props: results,
    revalidate: 60 * 60 * 24 * 30, // 30 days
  }
}

export default IndexPage
